import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import { CollapsibleSection, PrimaryBtn, SecondaryBtn, StatusPill } from '../systemKit';
import {
  fmtSignedPct, fmtCompact, annualizeImpact,
  DRAFT_SYSTEM_PROMPT, buildDraftDataBlock, buildFallbackDraft,
} from './utils';

// DistributorDrafts — AI-assisted outreach drafts for the items most worth
// negotiating. Picks top-3 movers by absolute dollar impact, generates an
// email subject + body for each (with hard "no invented facts" rule),
// shows them as editable cards. Operator can copy, send via mailto:, or
// archive. Drafts persist to distributor_drafts table.
//
// Static fallback if AI fails. Never blocks the section.

const MAX_DRAFTS = 3;

export default function DistributorDrafts({
  companyId,
  startDate,
  endDate,
  operatorFirstName,
  restaurantName,
}) {
  const [movers, setMovers] = useState(null);
  const [drafts, setDrafts] = useState({});  // food_item_id → {subject, body, status, id, vendor, generating, error, channel}
  const [contacts, setContacts] = useState({}); // vendor_name → contact_email

  // Pull top 3 movers (by impact) in window. Same data as TopMovers — cheap.
  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;
    let cancelled = false;
    setMovers(null);
    (async () => {
      const { data } = await supabase.rpc('cost_dashboard_top_impact_movers', {
        p_company_id: companyId,
        p_start_date: startDate,
        p_end_date: endDate,
        p_limit: MAX_DRAFTS,
      });
      if (cancelled) return;
      // Only fire on UP movers — we don't ask vendors to negotiate when
      // their prices are dropping.
      const ups = (data || []).filter(r => r.trend_direction === 'up' && Math.abs(Number(r.dollar_impact || 0)) >= 50);
      setMovers(ups);
    })();
    return () => { cancelled = true; };
  }, [companyId, startDate, endDate]);

  // Load vendor contacts once, in parallel with movers.
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data } = await supabase
        .from('vendor_contacts')
        .select('vendor_name, contact_email')
        .eq('company_id', companyId);
      const map = {};
      (data || []).forEach(c => { if (c.contact_email) map[c.vendor_name] = c.contact_email; });
      setContacts(map);
    })();
  }, [companyId]);

  // Load any persisted drafts for the in-scope movers; if missing, generate.
  useEffect(() => {
    if (!movers || movers.length === 0) return;
    let cancelled = false;
    (async () => {
      const ids = movers.map(m => m.food_item_id);
      const { data: existing } = await supabase
        .from('distributor_drafts')
        .select('id, food_item_id, vendor_name, subject, draft_body, status, channel, trigger_context')
        .eq('company_id', companyId)
        .in('food_item_id', ids)
        .eq('status', 'draft')
        .order('created_at', { ascending: false });

      if (cancelled) return;

      const byItem = {};
      (existing || []).forEach(d => {
        // Keep first (most recent) per food_item_id.
        if (!byItem[d.food_item_id]) byItem[d.food_item_id] = d;
      });

      const initial = {};
      const toGenerate = [];
      movers.forEach(m => {
        const persisted = byItem[m.food_item_id];
        if (persisted) {
          initial[m.food_item_id] = {
            id: persisted.id,
            vendor: persisted.vendor_name,
            subject: persisted.subject || '',
            body: persisted.draft_body || '',
            channel: persisted.channel || 'email',
            status: persisted.status,
            generating: false,
            error: null,
          };
        } else {
          initial[m.food_item_id] = { generating: true, vendor: m.primary_vendor, channel: 'email' };
          toGenerate.push(m);
        }
      });
      setDrafts(initial);

      // Fire AI generation in parallel for each missing draft.
      toGenerate.forEach(m => generateDraft(m));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movers, companyId]);

  async function generateDraft(mover) {
    const id = mover.food_item_id;
    try {
      const annual = annualizeImpact(mover.dollar_impact, startDate, endDate);
      const dataBlock = buildDraftDataBlock({
        itemName: mover.item_name,
        vendorName: mover.primary_vendor,
        baselinePrice: mover.baseline_unit_price,
        currentPrice: mover.current_unit_price,
        baselineDate: null,
        currentDate: null,
        unit: 'unit',
        deltaPct: mover.delta_pct,
        weeksElapsed: null,
        volume: mover.total_volume_in_window,
        volumeUnit: 'units',
        consecutiveIncreases: mover.consecutive_increases,
        annualImpact: annual,
        operatorFirstName,
        restaurantName,
      });

      const response = await fetch('/api/parse-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 800,
          system: DRAFT_SYSTEM_PROMPT,
          messages: [{ role: 'user', content: dataBlock }],
          _meta: { feature: 'cost-dashboard-draft', companyId },
        }),
      });

      const data = await response.json();
      let subject = '';
      let body = '';

      if (response.ok && !data.error) {
        const text = data.content?.[0]?.text || '';
        const parsed = tryParseJson(text);
        if (parsed?.subject && parsed?.body) {
          subject = parsed.subject;
          body = parsed.body;
        }
      }

      // Fallback if AI failed or returned malformed JSON.
      if (!subject || !body) {
        const fb = buildFallbackDraft({
          itemName: mover.item_name,
          vendorName: mover.primary_vendor,
          deltaPct: mover.delta_pct,
          operatorFirstName,
          restaurantName,
        });
        subject = fb.subject;
        body = fb.body;
      }

      const triggerContext = `${mover.item_name} from ${mover.primary_vendor || 'vendor'}: ${fmtSignedPct(mover.delta_pct)} change · impact ${fmtCompact(mover.dollar_impact)}`;

      // Persist as a fresh draft row.
      const { data: inserted } = await supabase
        .from('distributor_drafts')
        .insert({
          company_id: companyId,
          food_item_id: mover.food_item_id,
          vendor_name: mover.primary_vendor || 'Unknown vendor',
          trigger_context: triggerContext,
          channel: 'email',
          subject,
          draft_body: body,
          status: 'draft',
        })
        .select('id')
        .single();

      setDrafts(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          id: inserted?.id || null,
          subject,
          body,
          generating: false,
          error: null,
        },
      }));
    } catch (e) {
      // Total failure: render the static fallback locally without persisting,
      // so the operator still has something to copy.
      const fb = buildFallbackDraft({
        itemName: mover.item_name,
        vendorName: mover.primary_vendor,
        deltaPct: mover.delta_pct,
        operatorFirstName,
        restaurantName,
      });
      setDrafts(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          subject: fb.subject,
          body: fb.body,
          generating: false,
          error: 'AI generation failed; using fallback template.',
        },
      }));
    }
  }

  const updateDraft = async (foodItemId, patch) => {
    setDrafts(prev => ({ ...prev, [foodItemId]: { ...prev[foodItemId], ...patch } }));
    const draft = drafts[foodItemId];
    if (!draft?.id) return;
    const dbPatch = {};
    if ('subject' in patch) dbPatch.subject = patch.subject;
    if ('body' in patch) dbPatch.draft_body = patch.body;
    if ('channel' in patch) dbPatch.channel = patch.channel;
    if (Object.keys(dbPatch).length > 0) {
      await supabase.from('distributor_drafts')
        .update({ ...dbPatch, updated_at: new Date().toISOString() })
        .eq('id', draft.id);
    }
  };

  const markSent = async (foodItemId) => {
    const draft = drafts[foodItemId];
    if (!draft?.id) return;
    await supabase.from('distributor_drafts')
      .update({ status: 'sent', sent_at: new Date().toISOString() })
      .eq('id', draft.id);
    setDrafts(prev => ({ ...prev, [foodItemId]: { ...prev[foodItemId], status: 'sent' } }));
  };

  const archive = async (foodItemId) => {
    const draft = drafts[foodItemId];
    if (!draft?.id) return;
    await supabase.from('distributor_drafts')
      .update({ status: 'archived' })
      .eq('id', draft.id);
    setDrafts(prev => {
      const next = { ...prev };
      delete next[foodItemId];
      return next;
    });
  };

  const subhead = movers == null
    ? 'loading…'
    : movers.length === 0
      ? 'no movers worth a draft right now'
      : `${movers.length} draft${movers.length === 1 ? '' : 's'} based on this window`;

  const visibleDrafts = useMemo(() => {
    if (!movers) return [];
    return movers
      .map(m => ({ mover: m, draft: drafts[m.food_item_id] }))
      .filter(x => x.draft);
  }, [movers, drafts]);

  return (
    <CollapsibleSection
      id="cost-dashboard-distributor-drafts"
      title="Talk to your distributors"
      count={visibleDrafts.length > 0 ? `${visibleDrafts.length} drafts ready` : null}
      countTone="amber"
      subhead={subhead}
      defaultExpanded={false}
    >
      {!movers && <Skeleton />}
      {movers && movers.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, borderTop: '1px solid var(--border)' }}>
          No items moved enough in this window to draft outreach. Quiet weeks happen.
        </div>
      )}
      {visibleDrafts.length > 0 && (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--border)' }}>
          {visibleDrafts.map(({ mover, draft }) => (
            <DraftCard
              key={mover.food_item_id}
              mover={mover}
              draft={draft}
              vendorEmail={contacts[mover.primary_vendor || '']}
              onUpdate={(patch) => updateDraft(mover.food_item_id, patch)}
              onMarkSent={() => markSent(mover.food_item_id)}
              onArchive={() => archive(mover.food_item_id)}
            />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}

// ─── Card ───────────────────────────────────────────────────────────────────

function DraftCard({ mover, draft, vendorEmail, onUpdate, onMarkSent, onArchive }) {
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const channel = draft.channel || 'email';

  const triggerHeadline = `${mover.item_name} from ${(mover.primary_vendor || 'vendor').replace(/\n/g, ' · ')}: ${fmtSignedPct(mover.delta_pct)}`;

  const copyToClipboard = async () => {
    const text = `${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — old browsers
    }
  };

  const sendViaGmail = () => {
    const params = new URLSearchParams({
      su: draft.subject || '',
      body: draft.body || '',
    });
    if (vendorEmail) params.set('to', vendorEmail);
    const url = `https://mail.google.com/mail/?view=cm&fs=1&${params.toString()}`;
    window.open(url, '_blank', 'noopener');
    onMarkSent();
  };

  if (draft.generating) {
    return (
      <div style={{
        padding: 14,
        border: '1px solid var(--border)', borderRadius: 8,
        background: 'var(--bg-card)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
          {triggerHeadline}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Drafting…</div>
      </div>
    );
  }

  if (draft.status === 'sent') {
    return (
      <div style={{
        padding: 14,
        border: '1px solid var(--border)', borderRadius: 8,
        background: 'var(--bg-subtle)',
        opacity: 0.85,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {triggerHeadline}
          </div>
          <StatusPill tone="green">Sent</StatusPill>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: 14,
      border: '1px solid var(--border)', borderRadius: 8,
      background: 'var(--bg-card)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>
            Outreach draft
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            {triggerHeadline}
          </div>
          {draft.error && (
            <div style={{ fontSize: 11, color: 'var(--text-warning)', marginTop: 4 }}>
              {draft.error}
            </div>
          )}
        </div>
        <ChannelToggle
          value={channel}
          onChange={(c) => onUpdate({ channel: c })}
        />
      </div>

      {channel === 'in_person' ? (
        <TalkingPoints mover={mover} />
      ) : (
        <>
          {editing ? (
            <>
              <input
                type="text"
                value={draft.subject || ''}
                onChange={(e) => onUpdate({ subject: e.target.value })}
                style={{
                  width: '100%', padding: '8px 10px', marginBottom: 8,
                  fontSize: 13, fontWeight: 500,
                  background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text-primary)',
                }}
              />
              <textarea
                value={draft.body || ''}
                onChange={(e) => onUpdate({ body: e.target.value })}
                rows={7}
                style={{
                  width: '100%', padding: '8px 10px',
                  fontSize: 13, lineHeight: 1.5,
                  background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text-primary)',
                  fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                  padding: '6px 10px', background: 'var(--bg-subtle)',
                  borderRadius: 6, marginBottom: 8,
                }}
                title="Subject"
              >
                {draft.subject || '(no subject)'}
              </div>
              <div
                style={{
                  fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)',
                  padding: 10, background: 'var(--bg-subtle)',
                  borderRadius: 6, whiteSpace: 'pre-wrap',
                }}
              >
                {draft.body}
              </div>
            </>
          )}
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {channel !== 'in_person' && (
          <>
            <SecondaryBtn onClick={() => setEditing(e => !e)}>
              {editing ? 'Done' : 'Edit'}
            </SecondaryBtn>
            <SecondaryBtn onClick={copyToClipboard}>
              {copied ? 'Copied!' : 'Copy'}
            </SecondaryBtn>
            <PrimaryBtn onClick={sendViaGmail}>
              Send via Gmail
            </PrimaryBtn>
          </>
        )}
        <button
          onClick={onArchive}
          style={{
            marginLeft: 'auto',
            padding: '8px 12px', fontSize: 12,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-critical)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          Archive
        </button>
      </div>

      {!vendorEmail && channel === 'email' && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          No saved email for {(mover.primary_vendor || 'this vendor').replace(/\n/g, ' · ')}. The Send button opens Gmail compose without a To: address.
        </div>
      )}
    </div>
  );
}

function ChannelToggle({ value, onChange }) {
  const opts = [
    { key: 'email',     label: 'Email' },
    { key: 'in_person', label: 'In person' },
  ];
  return (
    <div style={{ display: 'inline-flex', background: 'var(--bg-subtle)', borderRadius: 6, padding: 2 }}>
      {opts.map(o => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            style={{
              padding: '4px 8px', fontSize: 11, fontWeight: 600,
              border: 'none',
              background: active ? 'var(--bg-card)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: 4, cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function TalkingPoints({ mover }) {
  const points = [
    `Item: ${mover.item_name}`,
    mover.delta_pct != null && `Price moved ${fmtSignedPct(mover.delta_pct)} vs. recent baseline`,
    mover.baseline_unit_price != null && mover.current_unit_price != null
      && `From $${Number(mover.baseline_unit_price).toFixed(2)} → $${Number(mover.current_unit_price).toFixed(2)}`,
    mover.dollar_impact != null && `In-window cost impact: ${fmtCompact(mover.dollar_impact)}`,
    Number(mover.consecutive_increases) >= 3 && `${mover.consecutive_increases}× consecutive raises observed`,
    'Ask: Is there room to revisit pricing?',
  ].filter(Boolean);

  return (
    <ul style={{
      padding: 12, background: 'var(--bg-subtle)', borderRadius: 6,
      fontSize: 13, color: 'var(--text-primary)',
      listStyle: 'disc', listStylePosition: 'inside',
      lineHeight: 1.6,
    }}>
      {points.map((p, i) => <li key={i}>{p}</li>)}
    </ul>
  );
}

function tryParseJson(text) {
  if (!text) return null;
  // Strip markdown fences if present.
  const cleaned = text.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim();
  try { return JSON.parse(cleaned); } catch { return null; }
}

function Skeleton() {
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--border)' }}>
      {[0, 1].map(i => (
        <div key={i} style={{
          padding: 14, border: '1px solid var(--border)', borderRadius: 8,
        }}>
          <div style={{ height: 12, width: '60%', background: 'var(--bg-subtle)', borderRadius: 4, marginBottom: 12 }} />
          <div style={{ height: 64, background: 'var(--bg-subtle)', borderRadius: 6 }} />
        </div>
      ))}
    </div>
  );
}
